import React, { useState, useEffect, useContext } from "react";
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
} from "react-icons/fa";
import { Breadcrumb, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { login } from "../../utils/auth";
import { RootContext } from "../../context/RootContext";
import { useNavigate } from "react-router-dom";
import ForgotPasswordModal from "./forgotPassword";

const LoginPage = () => {
  const navigate = useNavigate();
  const { dispatch } = useContext(RootContext);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [focusedField, setFocusedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTrialExpired, setShowTrialExpired] = useState(false);
  const [trialExpiredMsg, setTrialExpiredMsg] = useState("");
  const [showForgot, setShowForgot] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      return message.warning("Please enter credentials");
    }

    setLoading(true);

    try {
      const { data, error } = await login(username, password, true, "configuration");

      if (error) {
        const status = error.response?.status;

        if (status === 403) {
          setTrialExpiredMsg(
            error.response?.data?.message ||
            "Your free trial has expired. Please subscribe to continue using CogniVision."
          );
          setShowTrialExpired(true);
          return;
        }

        if (status === 401) {
          return message.error("Invalid username or password");
        }

        return message.error(
          error.response?.data?.message || "User not authorized"
        );
      }

      message.success("Login Successful");

      dispatch({
        type: "LOGIN",
        user: data.user,
        admin: data.admin,
      });

      navigate("/create-project");

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const citriot = JSON.parse(localStorage.getItem("citriot"));
    if (citriot?.auth?.isLoggedIn) {
      navigate("/create-project");
      // navigate("/home");
    }
  }, [navigate]);

  const TrialExpiredModal = ({ message: msg, onClose }) => {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative z-10 w-full max-w-sm bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 overflow-hidden animate-modal-in">
          {/* Top gradient bar — amber/orange for warning */}
          <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 to-purple-600 to-red-500" />

          <div className="p-8 flex flex-col items-center text-center gap-5">
            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center shadow-lg shadow-amber-100">
              <svg
                className="w-8 h-8 text-amber-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>

            {/* Title */}
            <div>
              <h2 className="text-xl font-bold text-slate-800 mb-1">
                Free Trial Expired
              </h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                {msg ||
                  "Your free trial has expired. Please subscribe to continue using CogniVision."}
              </p>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-slate-100" />

            {/* Actions */}
            <div className="flex flex-col gap-3 w-full">
              {/* <button
                onClick={() => {
                  // 🔁 Replace with your actual subscription/pricing route
                  // window.open("https://citriot.com/pricing", "_blank");
                }}
                className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-xl text-white font-semibold text-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-xl flex items-center justify-center gap-2"
              >
                <FaBolt className="w-3.5 h-3.5" />
                Upgrade Now
              </button> */}

              <button
                onClick={onClose}
                className="w-full h-11 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-600 font-semibold text-sm transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes modal-in {
            from { opacity: 0; transform: scale(0.92) translateY(16px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
          .animate-modal-in { animation: modal-in 0.3s cubic-bezier(0.34,1.56,0.64,1) both; }
        `}</style>
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-4 relative overflow-hidden">
        <div className="absolute top-6 left-6 z-30">
          <Breadcrumb>
            <Breadcrumb.Item
              onClick={() => navigate("/landing-page")}
              className="cursor-pointer text-slate-600 hover:text-blue-600 font-medium"
            >
              <ArrowLeftOutlined className="mr-1" />
              Back
            </Breadcrumb.Item>
            <Breadcrumb.Item className="text-slate-400">
              Configuration Login
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>

        {/* Background blobs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-200 rounded-full blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-cyan-200 rounded-full blur-xl opacity-30 animate-pulse"></div>

        <div className="w-full max-w-md relative z-10">
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-slate-200/50">

            {/* 🔹 Breadcrumb */}
            {/* <div className="mb-4">
              <Breadcrumb>
                <Breadcrumb.Item
                  className="cursor-pointer text-slate-600 hover:text-blue-600"
                  onClick={() => navigate("/login")}
                >
                  <ArrowLeftOutlined className="mr-1" />
                  Back
                </Breadcrumb.Item>
              </Breadcrumb>
            </div> */}

            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
                <FaShieldAlt className="text-white text-3xl" />
              </div>
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-800 mb-2">
                Welcome Back
              </h1>
              <p className="text-slate-500 text-sm">
                Sign in to access your dashboard
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Username */}
              <div>
                <label className="text-sm font-medium text-slate-700">Username</label>
                <div className="relative mt-1">
                  <FaUser
                    className={`absolute left-4 top-1/2 -translate-y-1/2 ${
                      focusedField === "username" ? "text-blue-500" : "text-slate-400"
                    }`}
                  />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onFocus={() => setFocusedField("username")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter your username"
                    className="w-full h-12 pl-12 pr-4 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-400 focus:outline-none"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="text-sm font-medium text-slate-700">Password</label>
                <div className="relative mt-1">
                  <FaLock
                    className={`absolute left-4 top-1/2 -translate-y-1/2 ${
                      focusedField === "password" ? "text-cyan-500" : "text-slate-400"
                    }`}
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField("password")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter your password"
                    className="w-full h-12 pl-12 pr-12 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-cyan-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition"
              >
                {loading ? "Authenticating..." : "Access Dashboard"}
              </button>
            </form>

<div className="mt-8 text-center space-y-4">
            <button
                      onClick={() => setShowForgot(true)}
                      className="text-sm text-slate-500 hover:text-blue-500 transition-colors duration-300 relative group"
                    >
                      <span className="relative">
                        Forgot Password?
                        <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300" />
                      </span>
                    </button>

            <p className="text-xs text-center text-slate-400 mt-6">
              Secured by advanced authentication
            </p>
            </div>
          </div>
        </div>
      </div>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}

      {showTrialExpired && (
        <TrialExpiredModal
          message={trialExpiredMsg}
          onClose={() => setShowTrialExpired(false)}
        />
      )}
    </>
  );
};

export default LoginPage;